// ITSM ticketing console (§23) — service tickets.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Ticket as TicketIcon, Plus } from 'lucide-react';
import { ticketingApi } from '@/lib/ticketing-api';
import { inventoryApi, type ServiceTicket } from '@/lib/inventory-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { StatusPill, PriorityPill, KpiStat } from '@/components/ui/primitives';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { NewTicketModal } from '@/components/NewTicketModal';

const TYPES = ['All', 'Incident', 'Service Request', 'Maintenance', 'Change Request', 'Problem'];
const LIFECYCLE = ['New', 'Assigned', 'In Progress', 'Pending', 'Resolved', 'Closed'];

const isClosed = (s: string) => /^(closed|resolved)$/i.test(s);

/** Map an ITSM ticket status to a StatusPill status token (drives tone + icon). */
function pillStatus(status: string): string {
  const s = status.toLowerCase();
  if (s === 'closed') return 'closed';
  if (s === 'resolved') return 'resolved';
  if (s === 'escalated' || s === 'pending') return 'warning';
  if (s === 'in progress' || s === 'in_progress') return 'in_progress';
  if (s === 'assigned') return 'active';
  return s; // 'new' and anything else -> muted pill
}

function SlaSourceChip({ source }: { source: string | null }) {
  if (!source) return <span className="text-muted">—</span>;
  const tone: BadgeTone = source === 'contract' ? 'info' : 'neutral';
  return <Badge tone={tone}>{source}</Badge>;
}

function SlaCell({ ticket }: { ticket: ServiceTicket }) {
  const { t } = useTranslation('pages');
  if (ticket.slaBreached) return <span className="text-xs font-medium text-critical">{t('tickets.sla.breached')}</span>;
  if (ticket.escalationLevel > 0) return <span className="tk-num text-xs text-warning">L{ticket.escalationLevel}</span>;
  return <span className="text-xs text-ok">{t('tickets.sla.ok')}</span>;
}

export function TicketsPage() {
  const { t } = useTranslation('pages');
  const [type, setType] = useState('All');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ?new=1&assetId=<uuid> (asset detail / QR flow) auto-opens the create modal
  // pre-linked to that asset; its site/system-type seed the routing preview.
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillAssetId = searchParams.get('assetId') ?? undefined;
  const [addOpen, setAddOpen] = useState(searchParams.get('new') === '1');
  const { data: prefillAsset, isError: prefillError } = useQuery({
    queryKey: ['asset', prefillAssetId],
    queryFn: () => ticketingApi.getAsset(prefillAssetId!),
    enabled: addOpen && !!prefillAssetId,
    retry: false,
  });
  // A stale/deleted/cross-tenant ?assetId= that 404s must not wedge the modal
  // (or the header button) shut — drop the bad param so it opens blank instead.
  useEffect(() => {
    if (prefillError && prefillAssetId) setSearchParams({}, { replace: true });
  }, [prefillError, prefillAssetId, setSearchParams]);
  const closeModal = () => {
    setAddOpen(false);
    if (searchParams.get('new')) setSearchParams({}, { replace: true });
  };

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['service-tickets'],
    queryFn: inventoryApi.serviceTickets,
    refetchInterval: 10000,
  });

  const all: ServiceTicket[] = tickets ?? [];
  const rows = all.filter((t) => type === 'All' || t.type === type);

  const open = all.filter((t) => !isClosed(t.status)).length;
  const p1 = all.filter((t) => t.priority.toLowerCase() === 'critical' || t.priority.toUpperCase() === 'P1').length;
  const breached = all.filter((t) => t.slaBreached).length;
  const escalated = all.filter((t) => t.escalationLevel > 0).length;
  const resolved = all.filter((t) => /^(resolved|closed)$/i.test(t.status)).length;

  const columns: Column<ServiceTicket>[] = [
    { key: 'ticketId', header: t('tickets.columns.ticket'), className: 'tk-num font-medium whitespace-nowrap' },
    { key: 'title', header: t('tickets.columns.title'), grow: true },
    {
      key: 'type', header: t('tickets.columns.type'), hideable: true,
      render: (ticket) => <Badge>{ticket.type}</Badge>,
    },
    { key: 'priority', header: t('tickets.columns.priority'), align: 'center', render: (ticket) => <PriorityPill priority={ticket.priority} /> },
    { key: 'status', header: t('tickets.columns.status'), render: (ticket) => <StatusPill status={pillStatus(ticket.status)} label={ticket.status} /> },
    { key: 'site', header: t('tickets.columns.site'), hideable: true, render: (ticket) => <span className="text-muted">{ticket.site}</span> },
    { key: 'customer', header: t('tickets.columns.customer'), hideable: true, render: (ticket) => <span className="text-muted">{ticket.customer}</span> },
    { key: 'slaSource', header: t('tickets.columns.slaSource'), hideable: true, render: (ticket) => <SlaSourceChip source={ticket.slaSource} /> },
    {
      key: 'technician', header: t('tickets.columns.technician'), hideable: true,
      render: (ticket) => ticket.technician ? <span>{ticket.technician}</span> : <span className="text-muted">{t('tickets.unassigned')}</span>,
    },
    { key: 'vendor', header: t('tickets.columns.vendor'), hideable: true, defaultHidden: true, render: (ticket) => <span className="text-muted">{ticket.vendor ?? '—'}</span> },
    { key: 'sla', header: t('tickets.columns.sla'), align: 'right', render: (ticket) => <SlaCell ticket={ticket} /> },
    {
      key: 'createdAt', header: t('tickets.columns.created'), align: 'right', hideable: true,
      render: (ticket) => <span className="tk-num text-muted">{new Date(ticket.createdAt).toLocaleString()}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('tickets.title')}
        subtitle={t('tickets.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('tickets.actions.newTicket')}</button>}
      />

      {/* When prefilled from an asset, wait for the asset row so initial state
          seeds site/device/system-type in one shot (useState reads it once).
          If the fetch errors, open blank rather than staying stuck. */}
      {addOpen && (!prefillAssetId || prefillAsset || prefillError) && (
        <NewTicketModal
          initial={prefillAsset ? {
            assetId: prefillAsset.id,
            siteId: prefillAsset.siteId,
            deviceId: prefillAsset.deviceId,
            systemTypeId: prefillAsset.systemTypeId,
            title: t('tickets.assetFaultTitle', { name: prefillAsset.name }),
          } : undefined}
          onClose={closeModal}
          onCreated={() => { closeModal(); queryClient.invalidateQueries({ queryKey: ['service-tickets'] }); }}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiStat label={t('tickets.kpi.open')} value={open} tone={open ? 'warning' : 'ok'} />
        <KpiStat label={t('tickets.kpi.p1Critical')} value={p1} tone={p1 ? 'critical' : 'ok'} />
        <KpiStat label={t('tickets.kpi.slaBreached')} value={breached} tone={breached ? 'critical' : 'ok'} />
        <KpiStat label={t('tickets.kpi.escalated')} value={escalated} tone={escalated ? 'warning' : 'ok'} />
        <KpiStat label={t('tickets.kpi.resolved')} value={resolved} tone="ok" />
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2">
        {TYPES.map((typeOption) => {
          const n = typeOption === 'All' ? all.length : all.filter((x) => x.type === typeOption).length;
          const label = typeOption === 'All' ? t('tickets.types.all') : typeOption;
          return (
            <button
              key={typeOption}
              onClick={() => setType(typeOption)}
              className={`rounded-full px-3 py-1 text-xs transition-colors ${
                type === typeOption ? 'bg-primary/20 text-primary ring-1 ring-primary/40' : 'text-muted hover:bg-white/5 hover:text-fg'
              }`}
            >
              {label} ({n})
            </button>
          );
        })}
      </div>

      {/* Lifecycle legend */}
      <div className="tk-card flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="font-medium text-fg">{t('tickets.lifecycle.heading')}</span>
        {LIFECYCLE.map((state, i) => (
          <span key={state} className="flex items-center gap-2">
            <Badge>{t(`tickets.lifecycle.states.${state.toLowerCase().replace(/\s+/g, '_')}`)}</Badge>
            {i < LIFECYCLE.length - 1 && <span className="text-muted">→</span>}
          </span>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        tableId="tickets"
        pageSize={25}
        onRowClick={(t) => navigate(`/tickets/${t.ticketId}`)}
        emptyIcon={TicketIcon}
        emptyTitle={t('tickets.empty.title')}
        emptyHint={t('tickets.empty.hint')}
      />
    </div>
  );
}
